import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { LandingNav } from '@/components/landing/LandingNav';
import {
  Eye, Radar, Brain, Compass, Activity, Euro, Waves, PieChart, AlertTriangle, Sparkles,
  ArrowRight, ArrowUpRight, Check, Star, TrendingUp, ShieldCheck, Clock, Building2,
  Linkedin, Twitter, Mail, CheckCircle2, CalendarCheck,
} from 'lucide-react';

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const SectionLabel = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-cyan-400">
    <span className="w-6 h-px bg-cyan-400/60" /> {children}
  </span>
);

const Landing = () => {
  const { tx, lang } = useApp();
  const navigate = useNavigate();

  const pillars = [
    { icon: Eye, t: { es: 'Visibilidad', en: 'Visibility' }, q: { es: '¿Qué está ocurriendo?', en: 'What is happening?' } },
    { icon: Radar, t: { es: 'Análisis', en: 'Analysis' }, q: { es: '¿Por qué ocurre?', en: 'Why is it happening?' } },
    { icon: Brain, t: { es: 'Predicción', en: 'Prediction' }, q: { es: '¿Qué ocurrirá?', en: 'What will happen?' } },
    { icon: Compass, t: { es: 'Asesoramiento', en: 'Advisory' }, q: { es: '¿Qué debo hacer?', en: 'What should I do?' } },
  ];

  const capabilities = [
    { icon: Activity, t: { es: 'Salud de la empresa', en: 'Company health' }, d: { es: 'Una puntuación global que entiende cualquiera, sin leer balances.', en: 'A single score anyone understands, no balance sheets required.' } },
    { icon: Euro, t: { es: 'Visión financiera', en: 'Financial overview' }, d: { es: 'Cuánto entra, cuánto sale y qué queda, con contexto siempre.', en: 'What comes in, goes out and remains — always with context.' } },
    { icon: Waves, t: { es: 'Tesorería inteligente', en: 'Cash intelligence' }, d: { es: 'Proyección de caja a 90 días y avisos antes de la tensión.', en: '90-day cash projection and alerts before tension hits.' } },
    { icon: PieChart, t: { es: 'Rentabilidad real', en: 'Real profitability' }, d: { es: 'Descubre qué clientes y productos ganan (o pierden) dinero.', en: 'See which clients and products win (or lose) money.' } },
    { icon: AlertTriangle, t: { es: 'Alertas proactivas', en: 'Proactive alerts' }, d: { es: 'Detectamos riesgos y oportunidades antes de que ocurran.', en: 'We detect risks and opportunities before they happen.' } },
    { icon: Sparkles, t: { es: 'AI Advisor', en: 'AI Advisor' }, d: { es: 'Un asesor que responde en tu idioma, nunca una caja negra.', en: 'An advisor that answers in your language, never a black box.' } },
  ];

  const stats = [
    { v: '+2.400', l: { es: 'empresas asesoradas', en: 'companies advised' } },
    { v: '38 M€', l: { es: 'margen recuperado', en: 'margin recovered' } },
    { v: '4,9/5', l: { es: 'satisfacción media', en: 'average satisfaction' } },
    { v: '24/7', l: { es: 'monitorización', en: 'monitoring' } },
  ];

  const team = [
    { name: 'Xavier Esgueva', role: { es: 'CEO & Fundador', en: 'CEO & Founder' }, img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' },
    { name: 'Laura Méndez', role: { es: 'Directora de Asesoría', en: 'Head of Advisory' }, img: 'https://images.unsplash.com/photo-1685760259914-ee8d2c92d2e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' },
    { name: 'Carlos Ruiz', role: { es: 'CTO', en: 'CTO' }, img: 'https://images.unsplash.com/photo-1652471943570-f3590a4e52ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' },
    { name: 'Ana Torres', role: { es: 'Head of Product', en: 'Head of Product' }, img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&q=85&w=600' },
  ];

  const articles = [
    { cat: { es: 'Guía', en: 'Guide' }, t: { es: 'Cómo leer la salud financiera de tu empresa en 5 minutos', en: 'Read your company health in 5 minutes' }, min: 6, img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
    { cat: { es: 'Tesorería', en: 'Cash' }, t: { es: '5 señales de tensión de tesorería que no deberías ignorar', en: '5 cash tension signals you should not ignore' }, min: 4, img: 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
    { cat: { es: 'Análisis', en: 'Analysis' }, t: { es: 'Por qué facturar más no siempre significa ganar más', en: 'Why billing more is not always earning more' }, min: 7, img: 'https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
    { cat: { es: 'IA', en: 'AI' }, t: { es: 'IA en las finanzas de la pyme: del dato a la decisión', en: 'AI in SMB finance: from data to decision' }, min: 5, img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800' },
  ];

  return (
    <div className="landing-root overflow-x-hidden">
      <LandingNav />

      {/* HERO */}
      <section id="hero" className="relative min-h-screen flex items-center pt-28 pb-20">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/finpulse-bg.jpg)` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(8,11,30,0.92) 0%, rgba(10,12,40,0.72) 45%, rgba(12,10,40,0.5) 100%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-xs font-medium text-cyan-300 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-ring relative" /> {tx({ es: 'Inteligencia financiera + asesoramiento humano', en: 'Financial intelligence + human advisory' })}
              </span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              {tx({ es: 'La inteligencia financiera que tu empresa', en: 'The financial intelligence your company' })}{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">{tx({ es: 'merece', en: 'deserves' })}</span>.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }} className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
              {tx({ es: 'Entiende cómo está tu empresa y sabe exactamente qué hacer. Sin jerga contable, sin cajas negras. Datos, análisis y un equipo asesor real.', en: 'Understand how your company is doing and know exactly what to do. No accounting jargon, no black boxes. Data, analysis and a real advisory team.' })}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.19 }} className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })} data-testid="hero-demo-btn" className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white font-semibold transition-colors shadow-xl shadow-cyan-500/25">
                {tx({ es: 'Solicitar una demo', en: 'Book a demo' })} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button onClick={() => navigate('/login')} data-testid="hero-login-btn" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-white/15 hover:bg-white/[0.08] text-white font-medium transition-colors">
                {tx({ es: 'Explorar la plataforma', en: 'Explore the platform' })}
              </button>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-8 flex items-center gap-4 text-sm text-zinc-400">
              <div className="flex -space-x-2">
                {team.map((m) => <img key={m.name} src={m.img} alt="" className="w-8 h-8 rounded-full border-2 border-[#0A0E24] object-cover" />)}
              </div>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /> 4,9/5 · +2.400 {tx({ es: 'empresas', en: 'companies' })}</span>
            </motion.div>
          </div>

          {/* Floating preview */}
          <motion.div initial={{ opacity: 0, scale: 0.94, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative hidden lg:block">
            <div className="rounded-3xl bg-white/[0.06] border border-white/10 backdrop-blur-xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-widest">{tx({ es: 'Estado general', en: 'Overall status' })}</p>
                  <p className="font-heading text-2xl font-bold">NovaTech Solutions</p>
                </div>
                <div className="text-right">
                  <p className="font-heading text-4xl font-extrabold text-emerald-400 font-mono-num">72</p>
                  <p className="text-xs text-zinc-400">/ 100 · {tx({ es: 'Estable', en: 'Stable' })}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[{ l: 'Facturación', v: '1,24 M€', up: true }, { l: 'Beneficio', v: '141 k€', up: false }, { l: 'Tesorería', v: '486 k€', up: true }].map((k) => (
                  <div key={k.l} className="rounded-xl bg-white/[0.05] border border-white/10 p-3">
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider truncate">{k.l}</p>
                    <p className="font-mono-num font-bold text-sm mt-1">{k.v}</p>
                    <span className={`text-[10px] font-semibold ${k.up ? 'text-emerald-400' : 'text-rose-400'}`}>{k.up ? '▲' : '▼'} {k.up ? '+12%' : '-6%'}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-white/10 p-4">
                <div className="flex items-center gap-2 mb-1.5"><Sparkles className="w-4 h-4 text-cyan-300" /><span className="text-xs font-semibold text-cyan-200">{tx({ es: 'Insight del Advisor', en: 'Advisor insight' })}</span></div>
                <p className="text-sm text-zinc-200 leading-relaxed">{tx({ es: 'Tu margen bajó un 6% pese a facturar más. Ajustar precios a 3 clientes recuperaría 42.000 €.', en: 'Your margin fell 6% despite higher revenue. Adjusting prices for 3 clients would recover €42,000.' })}</p>
              </div>
            </div>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute -top-5 -right-4 rounded-2xl bg-[#0b0e1c]/80 backdrop-blur-xl border border-white/10 px-4 py-3 shadow-xl">
              <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-emerald-400" /></div><div><p className="text-[10px] text-zinc-400">{tx({ es: 'Ahorro anual', en: 'Annual saving' })}</p><p className="font-mono-num font-bold text-sm">+42.000 €</p></div></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS / PLATAFORMA */}
      <section id="plataforma" className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <SectionLabel>{tx({ es: 'La plataforma', en: 'The platform' })}</SectionLabel>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">{tx({ es: 'De los datos a la decisión, en cuatro pasos', en: 'From data to decision, in four steps' })}</h2>
            <p className="mt-4 text-zinc-400">{tx({ es: 'FINPULSE convierte tus números en respuestas claras que puedes accionar hoy.', en: 'FINPULSE turns your numbers into clear answers you can act on today.' })}</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="relative h-full rounded-2xl bg-white/[0.04] border border-white/10 p-6 hover:bg-white/[0.07] transition-colors group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <p.icon className="w-6 h-6 text-cyan-300" />
                  </div>
                  <span className="font-mono-num text-xs text-zinc-500">0{i + 1}</span>
                  <h3 className="font-heading text-xl font-bold mt-1">{tx(p.t)}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{tx(p.q)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTO / CAPABILITIES */}
      <section id="producto" className="relative py-24 bg-white/[0.015] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-2xl mb-14">
            <SectionLabel>{tx({ es: 'Producto', en: 'Product' })}</SectionLabel>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">{tx({ es: 'Todo lo que necesitas para dirigir con confianza', en: 'Everything you need to lead with confidence' })}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((c, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="h-full rounded-2xl bg-[#0b0e1c]/60 border border-white/10 p-6 hover:border-cyan-400/30 hover:-translate-y-1 transition-all">
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center mb-4"><c.icon className="w-5 h-5 text-cyan-300" /></div>
                  <h3 className="font-heading text-lg font-semibold">{tx(c.t)}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{tx(c.d)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-8">
            <button onClick={() => navigate('/login')} className="inline-flex items-center gap-2 text-cyan-400 font-semibold hover:gap-3 transition-all">
              {tx({ es: 'Ver la plataforma en acción', en: 'See the platform in action' })} <ArrowUpRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* EMPRESA / COMPANY */}
      <section id="empresa" className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionLabel>{tx({ es: 'La empresa', en: 'The company' })}</SectionLabel>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">{tx({ es: 'Democratizamos la inteligencia financiera', en: 'We democratize financial intelligence' })}</h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">{tx({ es: 'Nacimos para que cualquier empresa, sin importar su tamaño, entienda sus finanzas y tome mejores decisiones. Combinamos tecnología de análisis avanzada con un equipo asesor humano que te acompaña de verdad.', en: 'We were born so that any company, regardless of size, can understand its finances and make better decisions. We combine advanced analytics technology with a human advisory team that truly supports you.' })}</p>
            <div className="mt-6 space-y-3">
              {[{ es: 'Sin necesidad de saber contabilidad', en: 'No accounting knowledge required' }, { es: 'Cada insight explica el porqué', en: 'Every insight explains the why' }, { es: 'Datos seguros y privados', en: 'Secure and private data' }].map((f, i) => (
                <div key={i} className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-cyan-400 shrink-0" /><span className="text-zinc-300">{tx(f)}</span></div>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((s, i) => (
                <div key={i}>
                  <p className="font-heading text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent font-mono-num">{s.v}</p>
                  <p className="text-xs text-zinc-400 mt-1">{tx(s.l)}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="FINPULSE team" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 rounded-2xl bg-[#0b0e1c]/85 backdrop-blur-xl border border-white/10 px-5 py-4 shadow-xl">
                <div className="flex items-center gap-3"><ShieldCheck className="w-8 h-8 text-emerald-400" /><div><p className="font-heading font-bold">ISO 27001</p><p className="text-xs text-zinc-400">{tx({ es: 'Seguridad certificada', en: 'Certified security' })}</p></div></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EQUIPO / TEAM */}
      <section id="equipo" className="relative py-24 bg-white/[0.015] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <SectionLabel>{tx({ es: 'El equipo', en: 'The team' })}</SectionLabel>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">{tx({ es: 'Personas reales detrás de cada decisión', en: 'Real people behind every decision' })}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((m, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group rounded-2xl bg-[#0b0e1c]/60 border border-white/10 overflow-hidden hover:border-cyan-400/30 transition-colors">
                  <div className="relative h-64 overflow-hidden">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0b0e1c] via-transparent to-transparent" />
                    <div className="absolute bottom-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center"><Linkedin className="w-4 h-4 text-white" /></span>
                      <span className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center"><Twitter className="w-4 h-4 text-white" /></span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-bold">{m.name}</h3>
                    <p className="text-sm text-cyan-400">{tx(m.role)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONOCIMIENTO / KNOWLEDGE */}
      <section id="conocimiento" className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div className="max-w-2xl">
              <SectionLabel>{tx({ es: 'Centro de conocimiento', en: 'Knowledge center' })}</SectionLabel>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">{tx({ es: 'Aprende a dominar tus finanzas', en: 'Learn to master your finances' })}</h2>
            </div>
            <button onClick={() => toast(tx({ es: 'Centro de conocimiento (próximamente)', en: 'Knowledge center (coming soon)' }))} data-testid="knowledge-all" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-white/15 text-sm font-medium hover:bg-white/[0.08] transition-colors shrink-0">
              {tx({ es: 'Ver todos los recursos', en: 'View all resources' })} <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {articles.map((a, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <button onClick={() => toast(tx({ es: 'Artículo (demo)', en: 'Article (demo)' }))} data-testid={`article-${i}`} className="group text-left h-full w-full rounded-2xl bg-[#0b0e1c]/60 border border-white/10 overflow-hidden hover:-translate-y-1 hover:border-cyan-400/30 transition-all">
                  <div className="relative h-40 overflow-hidden">
                    <img src={a.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-cyan-500/90 text-white">{tx(a.cat)}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-semibold leading-snug group-hover:text-cyan-300 transition-colors">{tx(a.t)}</h3>
                    <p className="mt-3 flex items-center gap-1.5 text-xs text-zinc-500"><Clock className="w-3.5 h-3.5" /> {a.min} min · {tx({ es: 'lectura', en: 'read' })}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO */}
      <DemoSection />

      {/* FOOTER */}
      <Footer />
    </div>
  );
};

const DemoSection = () => {
  const { tx } = useApp();
  const [form, setForm] = useState({ name: '', company: '', email: '', date: '', message: '' });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error(tx({ es: 'Completa nombre, empresa y email.', en: 'Please fill in name, company and email.' }));
      return;
    }
    setSent(true);
    toast.success(tx({ es: '¡Solicitud enviada! Te contactaremos en 24h.', en: 'Request sent! We will contact you within 24h.' }));
  };

  const benefits = [
    { es: 'Demo personalizada de 30 minutos', en: '30-minute personalized demo' },
    { es: 'Con tus propios datos de ejemplo', en: 'With your own sample data' },
    { es: 'Sin compromiso ni tarjeta', en: 'No commitment, no card' },
  ];

  return (
    <section id="demo" className="relative py-24 border-t border-white/5">
      <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/finpulse-bg.jpg)` }} />
      <div className="absolute inset-0 bg-[#0A0E24]/80" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <SectionLabel>{tx({ es: 'Solicita tu demo', en: 'Book your demo' })}</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-4">{tx({ es: 'Ve FINPULSE con los ojos de tu empresa', en: 'See FINPULSE through your company’s eyes' })}</h2>
          <p className="mt-4 text-zinc-300">{tx({ es: 'Reserva una demo con nuestro equipo y descubre en minutos qué puede hacer FINPULSE por ti.', en: 'Book a demo with our team and discover in minutes what FINPULSE can do for you.' })}</p>
          <div className="mt-6 space-y-3">
            {benefits.map((b, i) => <div key={i} className="flex items-center gap-3"><div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-cyan-300" /></div><span className="text-zinc-200">{tx(b)}</span></div>)}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl bg-white/[0.06] border border-white/10 backdrop-blur-xl p-6 sm:p-8 shadow-2xl">
            {sent ? (
              <div className="text-center py-10" data-testid="demo-success">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4"><CalendarCheck className="w-8 h-8 text-emerald-400" /></div>
                <h3 className="font-heading text-2xl font-bold">{tx({ es: '¡Todo listo!', en: 'All set!' })}</h3>
                <p className="mt-2 text-zinc-400">{tx({ es: 'Hemos recibido tu solicitud. Nuestro equipo te contactará muy pronto.', en: 'We received your request. Our team will reach out very soon.' })}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="demo-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={tx({ es: 'Nombre', en: 'Name' })}><input value={form.name} onChange={set('name')} data-testid="demo-name" className="fp-input" placeholder="Xavi Esgueva" /></Field>
                  <Field label={tx({ es: 'Empresa', en: 'Company' })}><input value={form.company} onChange={set('company')} data-testid="demo-company" className="fp-input" placeholder="NovaTech" /></Field>
                </div>
                <Field label="Email"><input type="email" value={form.email} onChange={set('email')} data-testid="demo-email" className="fp-input" placeholder="xavi@empresa.com" /></Field>
                <Field label={tx({ es: 'Fecha preferida', en: 'Preferred date' })}><input type="date" value={form.date} onChange={set('date')} data-testid="demo-date" className="fp-input" /></Field>
                <Field label={tx({ es: 'Mensaje (opcional)', en: 'Message (optional)' })}><textarea value={form.message} onChange={set('message')} data-testid="demo-message" rows={3} className="fp-input resize-none" placeholder={tx({ es: 'Cuéntanos sobre tu empresa...', en: 'Tell us about your company...' })} /></Field>
                <button type="submit" data-testid="demo-submit" className="w-full h-12 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white font-semibold transition-colors shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2">
                  {tx({ es: 'Reservar mi demo', en: 'Book my demo' })} <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">{label}</label>
    {children}
  </div>
);

const Footer = () => {
  const { tx } = useApp();
  const navigate = useNavigate();
  const cols = [
    { h: { es: 'Producto', en: 'Product' }, items: [{ es: 'Plataforma', en: 'Platform' }, { es: 'Precios', en: 'Pricing' }, { es: 'Seguridad', en: 'Security' }] },
    { h: { es: 'Empresa', en: 'Company' }, items: [{ es: 'Sobre nosotros', en: 'About' }, { es: 'Equipo', en: 'Team' }, { es: 'Empleo', en: 'Careers' }] },
    { h: { es: 'Recursos', en: 'Resources' }, items: [{ es: 'Conocimiento', en: 'Knowledge' }, { es: 'Guías', en: 'Guides' }, { es: 'Soporte', en: 'Support' }] },
    { h: { es: 'Legal', en: 'Legal' }, items: [{ es: 'Privacidad', en: 'Privacy' }, { es: 'Términos', en: 'Terms' }, { es: 'Cookies', en: 'Cookies' }] },
  ];
  return (
    <footer className="relative border-t border-zinc-200 dark:border-white/10 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-5 gap-10 mb-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center"><Activity className="w-5 h-5 !text-white" strokeWidth={2.5} /></div>
              <span className="font-heading font-extrabold text-lg">FINPULSE</span>
            </div>
            <p className="text-sm landing-muted max-w-xs">{tx({ es: 'El centro financiero inteligente de tu empresa.', en: "Your company's intelligent financial hub." })}</p>
            <div className="flex gap-2 mt-4">
              {[Linkedin, Twitter, Mail].map((I, i) => <span key={i} className="w-9 h-9 rounded-lg landing-surface border flex items-center justify-center hover:opacity-80 transition-colors cursor-pointer"><I className="w-4 h-4" /></span>)}
            </div>
          </div>
          {cols.map((c, i) => (
            <div key={i}>
              <h4 className="font-heading font-semibold text-sm mb-4">{tx(c.h)}</h4>
              <ul className="space-y-2.5">
                {c.items.map((it, j) => <li key={j}><button onClick={() => navigate('/login')} className="text-sm landing-muted hover:text-cyan-600 dark:hover:text-cyan-300 transition-colors">{tx(it)}</button></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-zinc-200 dark:border-white/10">
          <p className="text-xs landing-muted">© 2026 FINPULSE · {tx({ es: 'Todos los derechos reservados', en: 'All rights reserved' })}</p>
          <button onClick={() => navigate('/login')} className="text-sm font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-500 dark:hover:text-cyan-300 flex items-center gap-1.5">{tx({ es: 'Acceder a la plataforma', en: 'Go to the platform' })} <ArrowUpRight className="w-4 h-4" /></button>
        </div>
      </div>
    </footer>
  );
};

export default Landing;
